import React from "react";
import { Column } from "../components/Layout";
import { Signature } from "../components/Signature";
import { FadeIn } from "../components/FadeIn";
import { OutlineLink } from "../components/OutlineLink";
import { Seo } from "../components/Seo";
import { SampleDownloadButton } from "../components/SampleDownloadButton";

const PLATE_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/52749592-c014-402b-8984-74e3e9024749/images/06f1af973702a5dac489fc08f2ad762fe716fc3c4b217113928a93775ae4b41f.png";

const LEDGER_IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/52749592-c014-402b-8984-74e3e9024749/images/d7ee6243dfe58b42070bb32eeb89844e2cb3652102335fac0895f0370f1a42aa.png";

const FromThePage = () => {
  return (
    <div data-testid="from-the-page">
      <Seo
        title="From the Page · A Life Study Specimen · The Raven's Quill"
        description="One page of the work itself: a demonstration passage, typeset as it appears in a finished Raven's Quill volume, written about an invented subject so that no client's life ever appears in public."
        path="/from-the-page"
      />
      <h1 className="sr-only">From the Page: A Life Study Specimen</h1>

      {/* THE FRAMING COPY */}
      <section className="pt-20 pb-14 md:pt-28 md:pb-16">
        <Column className="text-center">
          <Signature label="From the Page" testid="from-the-page-signature" />
          <FadeIn as="p" className="text-[17px] md:text-[18px] leading-[1.8] text-[#1C1A17]" data-testid="from-the-page-framing">
            What follows shows how the five instruments of the method, the
            conversations, the archive, the residency, the circle, and the image, are
            brought together on a single page, typeset as it would appear in a finished
            volume.
          </FadeIn>
        </Column>
      </section>

      {/* THE LEAF */}
      <section className="pb-16 md:pb-24">
        <div className="mx-auto max-w-[680px] px-6">
          <FadeIn>
            <div
              className="border border-[#7A2E2E] bg-[#FAF8F2] px-8 py-14 sm:px-14 sm:py-20"
              style={{ boxShadow: "0 10px 28px rgba(28,26,23,0.09)" }}
              data-testid="from-the-page-leaf"
              role="img"
              aria-label="A typeset page from a Life Study specimen volume"
            >
              {/* Running head */}
              <div className="text-center">
                <p className="text-[10px] uppercase tracking-[0.32em] text-[#6E6A5E]">
                  Esther Mokoena · A Life in Full
                </p>
                <hr className="mx-auto mt-4 border-0" style={{ width: "56px", height: "0.5px", backgroundColor: "#A8A296" }} />
              </div>

              {/* Chapter opening (roughly one third down) */}
              <div className="text-center mt-28 sm:mt-36">
                <p className="text-[11px] uppercase tracking-[0.32em] text-[#7A2E2E]">Chapter Four</p>
                <h2 className="mt-6 text-[26px] md:text-[30px] italic leading-[1.3] text-[#1C1A17]">
                  The Ledger and the Left Hand
                </h2>
                <hr className="mx-auto mt-6 border-0" style={{ width: "56px", height: "0.75px", backgroundColor: "#7A2E2E" }} />
              </div>

              {/* The passage: book setting */}
              <div className="mt-12 text-[18px] leading-[1.7] text-[#1C1A17]">
                {/* THE LEDGER (moved from The Method) */}
                <figure className="mb-12 mx-auto max-w-[380px]" data-testid="from-the-page-ledger">
                  <div className="border border-[#A8A296]">
                    <div className="bg-white p-2">
                      <img
                        src={LEDGER_IMAGE}
                        alt="A 1976 black hardcover Croxley book, closed and aged, its red spine mended twice with strips of surgical tape, its corners softly worn."
                        className="w-full h-auto"
                        loading="lazy"
                      />
                    </div>
                  </div>
                  <figcaption className="mt-4 text-center">
                    <p className="text-[13px] italic leading-[1.6] text-[#6E6A5E]">
                      The transport ledger, Pretoria North, 2026. Photographer: Zukiswa Phoofolo.
                    </p>
                  </figcaption>
                </figure>
                <p>
                  The ledger is the first thing she shows me and the last thing she lets
                  me touch. A hardcover Croxley book, 1976, the spine mended twice with
                  what looks like surgical tape. Every entry is in blue ink, in the
                  compressed, forward-leaning hand of a woman recording money she could
                  not afford to lose. Except one. On the ninth page, in black ink, in a
                  looser hand, as if written standing up, or shaking: Bus. Paid. God help
                  us.
                </p>
                <p className="[text-indent:1.6em]">
                  For three weeks I believed that entry was the beginning of the story.
                  The archive said so. The family said so. She had said so herself, weeks
                  before I ever entered the house, in the third of our recorded
                  conversations, her voice steady down the line: we paid for the bus, and
                  then we fetched it. In that order, she said. You can write it in that
                  order. And the framed photograph in the hallway says so too: Esther
                  Mokoena, thirty-one years old, in her church dress on a working morning,
                  her arm at full stretch, her hand on the bonnet of a 1974 Bedford. Not
                  leaning on it. Not resting. Touching it the way you touch a thing for
                  the record, so that the photograph will have to say: mine. Behind her,
                  the street has stopped walking.
                </p>

                {/* THE PLATE */}
                <figure className="my-12 mx-auto max-w-[380px]" data-testid="from-the-page-plate">
                  <div className="border border-[#A8A296]">
                    <div className="bg-white p-2">
                      <img
                        src={PLATE_IMAGE}
                        alt="A demonstration photograph in period style: a woman in 1970s dress stands beside a 1974 Bedford bus, her face turned away, her hand flat on the bonnet."
                        className="w-full h-auto grayscale"
                        loading="lazy"
                      />
                    </div>
                  </div>
                  <figcaption className="mt-4 text-center">
                    <p className="text-[13px] italic leading-[1.6] text-[#6E6A5E]">
                      Esther Mokoena, Atteridgeville, 1977. Photographer: the dealer&rsquo;s brother.
                    </p>
                  </figcaption>
                </figure>

                <p className="[text-indent:1.6em]">Then I met Petrus Mahlangu.</p>

                {/* FIELD NOTES: second register, inset */}
                <div className="my-10 mx-auto max-w-[86%]" data-testid="from-the-page-fieldnotes">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#6E6A5E]">
                    Field Notes · Petrus&rsquo;s Stoep · Ga-Rankuwa · 16:40
                  </p>
                  <p className="mt-4 text-[15px] leading-[1.55] text-[#1C1A17]">
                    He is ninety-one and he drove the first bus for eleven years and he
                    laughed for a long time when I read him the ledger entry. She had
                    that bus four months before she paid for it, he said. Four months. I
                    drove it in the dark so the dealer&rsquo;s brother would not see it
                    on the Mabopane road. You write that down. She will deny it. Write it
                    down anyway.
                  </p>
                </div>

                <p className="[text-indent:1.6em]">I wrote it down. She denied it, and the denial is also true. In the world Esther Mokoena built, that bus was paid for from the moment she gave her word it would be; the four months were the bank catching up with her.</p>
                <p className="[text-indent:1.6em]">
                  And here is where a conventional biography would make its quiet,
                  forgivable mistake. It would choose. It would settle the four months,
                  privately, in the writer&rsquo;s room, and hand the family a clean
                  line, as if the point of listening to a life were to rule on it. But
                  the gap between the
                  ledger and Petrus is not an error waiting for correction. It is the
                  finding. The ledger was written for God. The bus was driven for the
                  bank. Both are true, and the woman who could hold both truths on the
                  same road, in the same dark, is the woman who turned one Bedford into
                  the largest black-owned transport fleet north of Pretoria, and no
                  account that flattens the contradiction has understood the first thing
                  about her.
                </p>
                <p className="[text-indent:1.6em]">
                  The recorded conversations that begin a book like this one began, in her
                  case, three weeks late. The tablet her children had bought her for the
                  calls was still in its box, the protective plastic still on the screen,
                  because a woman who has hired and fired auditors was not going to tell a
                  stranger that a machine had declined her instructions. Three weeks of
                  calls were attributed to weather, to church, and to the funeral of
                  someone she later conceded, laughing, she did not know well. Then her
                  grandson Katlego, who calls her Koko and nothing else, came for Sunday
                  lunch, peeled the plastic off, downloaded the Google Meet app no one had
                  thought to check for, and we spoke that same evening for two hours.
                </p>
                <p className="[text-indent:1.6em]">
                  She is eighty-four now, and her mornings run to a timetable no one set
                  but her. Five o&rsquo;clock, the teacups, rinsed before the helper
                  arrives, because, she tells me on my ninth day in the house, no one who
                  works for me will ever find my dirt. She means the teacups. She also
                  means everything else in this book. I laughed anyway, because my own
                  mother says the same thing, in the same tone, and means it the same two
                  ways. Six o&rsquo;clock, the window, opened onto the road, where she
                  counts the first buses out by sound alone and can tell from the pitch of
                  a gearbox which driver is running late. Seven o&rsquo;clock, the depot
                  telephones with the morning report, a courtesy nobody has dared to
                  retire; she takes it standing, the way she has taken it for fifty years.
                  The grandchildren who run the fleet now send her the day&rsquo;s numbers
                  each evening, and the ones who run nothing but their own lives, in London
                  and in Perth, send photographs instead, which she reads the way she once
                  read ledgers, line by line, for what they hold and for what they leave
                  out. A woman who once ran forty buses to the minute now runs a morning
                  the same way, and the cargo is what it always was: people, carried.
                </p>
                <p className="[text-indent:1.6em]">
                  The telephone has a grammar too, and I have learned it. WhatsApp is for
                  family, and the family group is a register. When she sends the garden to
                  it, the roses she planted the year the second depot opened, she counts
                  the replies the way she once counted the evening takings, and a child who
                  has read and not answered by supper gets a telephone call to establish
                  whether he is eating. Two grey ticks is delivered. Two blue ticks is
                  read. Read and silent is a matter for the telephone. Voice notes are for
                  blessings and for instructions, in that order. The burial society, which
                  she could by now buy outright and would never leave, gets its
                  subscription hand-delivered on the first of the month, because belonging
                  is not a debit order. And the proportions of the house say what the
                  archive says: one photograph of a bus in the hallway, and a wall of
                  graduations, weddings, and babies everywhere else. Ask her about the
                  fleet and she gives you dates. Ask her about Katlego&rsquo;s graduation
                  and she gives you the weather, the gown, the row, the seat, and what his
                  late grandfather would have said, word for word, in his voice.
                </p>
                <p className="[text-indent:1.6em]">
                  I write this down at the kitchen table while she watches me write it.
                  That is the arrangement. She knows where I am standing. I have told her.
                  I am the stranger she has allowed into the early morning of her life, and
                  everything on these pages was seen from that specific chair, at that
                  specific hour, by a writer she could watch writing.
                </p>
                <p className="[text-indent:1.6em]">
                  The photograph in the hallway was taken by the dealer&rsquo;s brother.
                  She told me that on the last morning, unprompted, drying a cup.
                </p>
                <p className="[text-indent:1.6em]">
                  Nobody else knows that, she said. Now you do. Do your job.
                </p>
              </div>

              {/* Folio */}
              <p className="mt-16 text-center text-[12px] text-[#6E6A5E]">47</p>
            </div>
          </FadeIn>
          <FadeIn className="mt-10 flex justify-center">
            <SampleDownloadButton
              targetTestId="from-the-page-leaf"
              filename="the-ravens-quill-life-study-sample.pdf"
              label="Download This Page as PDF"
              testid="from-the-page-download"
            />
          </FadeIn>
        </div>
      </section>

      {/* THE CLOSE */}
      <section className="pb-24 md:pb-32">
        <Column className="text-center">
          <FadeIn>
            <p className="text-[19px] md:text-[21px] italic leading-[1.6] text-[#1C1A17]" data-testid="from-the-page-close">
              This is how a life reads when it has been studied, not summarised.
            </p>
          </FadeIn>
          <FadeIn className="mt-12 flex flex-col sm:flex-row gap-4 sm:gap-5 justify-center">
            <OutlineLink to="/commission" testid="from-the-page-cta-consultation">
              Request a Private Consultation
            </OutlineLink>
            <OutlineLink to="/the-method" variant="oxblood" testid="from-the-page-cta-method">
              Read the Method
            </OutlineLink>
          </FadeIn>
        </Column>
      </section>
    </div>
  );
};

export default FromThePage;
